/**
 * Profile photo and byline display components for user identity rendering.
 *
 * `<ProfilePhoto>` selects the right sub-component based on profile data: a
 * remote photo URL, a letter avatar (a name initial on a colour hashed from
 * the user's key), or the anonymous fallback.
 * `<ProfilePhotoOverlays>` renders feature-injected overlay layers via
 * `profilePhotoLayers` in `StandardConfig`, so features like badges can augment
 * avatars without touching this file. `<Byline>` renders a photo + name +
 * optional metadata row; `<BylineMetadata>` reads `bylineTags` from config so
 * features can inject tag components (e.g. "verified") alongside the timestamp.
 *
 * Identity arrives exactly one way: a `profile` the caller already holds.
 * Nothing here fetches, so a caller holding only a key resolves it itself
 * (`useProfile`, or `useMyProfile` for the signed-in user) and passes the
 * result down. The profile carries its own `profileKey`, so nothing here takes
 * a separate user id — the avatar's colour and the byline's click-through both
 * come off the profile.
 */

import { IdManagement } from '@carbon/icons-react';
import type { IsoLocale } from '@repo/shared/locale';
import type { UserProfile } from '@repo/shared/orpc/types';
import { computeHueForKey } from '@repo/shared/persona-hue';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { useSettings } from 'data/settings';
import { getFontSize } from 'platform-specific/font';
import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { useDatastore } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import type { StandardConfig } from 'util/stdconfig';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { HoverView, Pad } from './basics';
import { spacings } from './constants';
import { fromNow } from './date';
import { IconCircleCheck } from './icon';
import { OriginalLanguageTag, useOriginalLanguage } from './tag/original-language-tag';
import { UtilityText } from './text';
import { useSiloLanguage, useTranslation } from './translation';
import { TextButton } from './ui/text-button';

export type ProfileSize = 'huge' | 'large' | 'medium' | 'small' | 'tiny';

export type ProfilePhotoProps = {
    profile: UserProfile | null;
    type?: ProfileSize;
    faint?: boolean;
    check?: boolean;
    border?: boolean;
    overlaysAlignment?: 'left' | 'right';
};

export function ProfilePhoto({
    profile,
    type = 'large' as ProfileSize,
    faint = false,
    check = false,
    border = false,
    overlaysAlignment,
}: ProfilePhotoProps) {
    return (
        <View>
            <ProfilePhotoImage border={border} check={check} faint={faint} profile={profile} type={type} />
            <ProfilePhotoOverlays alignment={overlaysAlignment} profile={profile} type={type} />
        </View>
    );
}

export type ProfilePhotoImageProps = {
    profile: UserProfile | null;
    type?: ProfileSize;
    faint?: boolean;
    check?: boolean;
    border?: boolean;
};

/** @public */
export function ProfilePhotoImage({
    profile,
    type = 'large' as ProfileSize,
    faint = false,
    check = false,
    border = false,
}: ProfilePhotoImageProps) {
    if (profile?.photoUrl) {
        return <FaceImage border={border} check={check} faint={faint} photoUrl={profile.photoUrl} type={type} />;
    }

    if (profile?.name) {
        return <LetterFace border={border} name={profile.name} type={type} userId={profile.profileKey} />;
    }

    return <AnonymousFace border={border} check={check} faint={faint} type={type} />;
}

export type ProfilePhotoOverlaysProps = { profile: UserProfile | null; type: ProfileSize; alignment?: 'left' | 'right' };

/** @public */
export function ProfilePhotoOverlays({ profile, type, alignment = 'right' }: ProfilePhotoOverlaysProps) {
    const { profilePhotoLayers } = useConfig<StandardConfig>();
    const size = sizeMap[type] ?? 32;

    return (
        <>
            {profilePhotoLayers &&
                profilePhotoLayers.map((item, i) => (
                    <View
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: size,
                            height: size,
                            backgroundColor: 'transparent',
                        }}
                    >
                        {React.createElement(item, { profile, size, type, alignment })}
                    </View>
                ))}
        </>
    );
}

export type FaceImageProps = { face?: string; photoUrl?: string; type?: ProfileSize; faint?: boolean; check?: boolean; border?: boolean };

export function FaceImage({ face, photoUrl, type = 'small' as ProfileSize, faint = false, check = false, border = false }: FaceImageProps) {
    const size = sizeMap[type] ?? 32;
    const checkPad = check ? checkPadMap[type ?? 'small'] : 0;

    return (
        <View style={{ position: 'relative', alignSelf: 'flex-start' }}>
            <Image
                source={{ uri: photoUrl ?? 'https://new-public-demo.web.app/faces/' + face }}
                style={{
                    width: size,
                    height: size,
                    borderRadius: size / 2,
                    opacity: faint ? 0.5 : 1,
                    marginRight: checkPad,
                    borderWidth: border ? 2 : 0,
                    borderColor: 'white',
                }}
            />
            {check && (
                <View style={{ position: 'absolute', right: 0, bottom: 0 }}>
                    <IconCircleCheck />
                </View>
            )}
        </View>
    );
}

export type LetterFaceProps = { name?: string; userId?: string | null; type?: ProfileSize; faint?: boolean; border?: boolean };

/**
 * The colored-initial fallback avatar. The colour is a hash of `userId`
 * (`computeHueForKey`), never chosen or stored, so the same user gets the
 * same colour everywhere without any hue crossing the wire.
 */
export function LetterFace({ name = '?', userId, type = 'large', faint = false, border }: LetterFaceProps) {
    const s = useLetterFaceStyle();
    const { avatarColors } = useTheme();
    const size = sizeMap[type] ?? 32;
    const color = avatarColors[computeHueForKey(userId ?? '') % avatarColors.length];
    const letter = name.substring(0, 1).toUpperCase();
    return (
        <View
            style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: color,
                opacity: faint ? 0.5 : 1,
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: border ? 2 : 0,
                borderColor: 'white',
            }}
        >
            <Text aria-hidden style={[{ fontSize: getFontSize(size / 2) }, s.letter]}>
                {letter}
            </Text>
        </View>
    );
}

const useLetterFaceStyle = createThemedStyles((tokens) => ({
    letter: {
        color: tokens.colors.colorTextOnAccentColorBg,
        fontFamily: tokens.fonts.fontFamilySansMedium,
    },
}));

export type AnonymousFaceProps = { faint?: boolean; type: ProfileSize; border: boolean; check: boolean };

/** @public */
export function AnonymousFace({ faint = false, type, border, check }: AnonymousFaceProps) {
    return <FaceImage border={border} check={check} face='anonymous2.jpeg' faint={faint} type={type} />;
}

export type FacePileProps = { type?: ProfileSize; profiles: readonly UserProfile[]; maxSize?: number };

export function FacePile({ type = 'small' as ProfileSize, profiles, maxSize = 3 }: FacePileProps) {
    const s = FacePileStyle;
    const size = sizeMap[type] ?? 32;

    return (
        <View style={s.outer}>
            {profiles.slice(0, maxSize).map((profile, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={`${profile.profileKey}-${i}`} style={{ position: 'relative', marginLeft: i === 0 ? 0 : -(size / 4) }}>
                    <ProfilePhoto border overlaysAlignment='left' profile={profile} type={type} />
                </View>
            ))}
        </View>
    );
}

const FacePileStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
    },
});

type TagRowProps = {
    tags: React.ReactNode[];
};

function TagRow({ tags }: TagRowProps) {
    const s = TagRowStyle;
    return (
        <View style={s.outer}>
            {tags.map((tag, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={i}>{tag}</View>
            ))}
        </View>
    );
}

const TagRowStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export type BylineMetadataProps = {
    profile: UserProfile | null;
    time?: number;
    edited?: boolean;
    originalLanguage?: IsoLocale | null;
    postId?: string;
};

/** @public */
export function BylineMetadata({ profile, time, edited = false, originalLanguage, postId }: BylineMetadataProps) {
    const { colors } = useTheme();
    const { bylineTags } = useConfig<StandardConfig>();
    const settings = useSettings();
    const language = useSiloLanguage();
    const showLanguageTag = useOriginalLanguage(originalLanguage) !== null;

    const tags = profile ? bylineTags?.map((tagFunc) => tagFunc(profile, { settings })).filter((tag) => tag) : [];
    const hasTags = tags && tags?.length > 0;
    const showSeparator = hasTags || showLanguageTag;

    const editedLabel = useTranslation(t('byline.edited', 'Edited'));
    const postIdLabel = useTranslation(t('byline.post_id', 'ID:'));
    // TODO: Support more than one tag when there are designs for that
    return (
        <View style={BylineMetadataStyle.outer}>
            {hasTags && <TagRow tags={[tags[0]]} />}
            {showLanguageTag && <OriginalLanguageTag originalLanguage={originalLanguage} />}
            {!!time && (
                <>
                    {showSeparator && <BylineSeparator />}
                    <UtilityText color={colors.colorTextSecondary} text={fromNow(time, language)} type='tiny' />
                </>
            )}
            {!!postId && (
                <>
                    {(showSeparator || !!time) && <BylineSeparator />}
                    <IdManagement color={colors.colorTextSecondary} size={12} />
                    <UtilityText color={colors.colorTextSecondary} text={`${postIdLabel} ${postId}`} type='tiny' />
                </>
            )}
            {!!time && edited && (
                <>
                    <BylineSeparator />
                    <UtilityText color={colors.colorTextSecondary} text={editedLabel} type='tiny' />
                </>
            )}
        </View>
    );
}

const BylineMetadataStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
});

function BylineSeparator() {
    const { colors } = useTheme();
    return (
        <Svg fill='none' height={3} viewBox='0 0 3 3' width={3}>
            <Circle cx={1.5} cy={1.5} fill={colors.colorTextSecondary} r={1.5} />
        </Svg>
    );
}

export type BylineProps = {
    type?: ProfileSize;
    clickable?: boolean;
    profile: UserProfile | null;
    time?: number;
    underline?: boolean;
    edited?: boolean;
    subtitleLabel?: TLabel;
    subtitleParams?: FormatParams;
    showMetadata?: boolean;
    onPress?: () => void;
    originalLanguage?: IsoLocale | null;
    postId?: string;
};

export function Byline({
    type = 'small' as ProfileSize,
    clickable = true,
    profile,
    time,
    underline = false,
    edited = false,
    subtitleLabel,
    subtitleParams,
    showMetadata = true,
    onPress,
    originalLanguage,
    postId,
}: BylineProps) {
    const { colors } = useTheme();
    const s = useBylineStyle();
    const datastore = useDatastore();
    const isDeleted = !!profile?.deleted;
    const deletedUserLabel = t('byline.deleted_user', 'Deleted User');
    const deletedUserText = useTranslation(deletedUserLabel);
    const displayName = profile?.name;
    const buttonText = isDeleted ? deletedUserText : displayName;
    function onProfile() {
        if (onPress) {
            return onPress();
        }
        if (profile && !isDeleted) {
            datastore.gotoInstance({ structureKey: 'profile', instanceKey: profile.profileKey });
        }
    }
    return (
        <View style={s.outer}>
            <ProfilePhoto aria-hidden profile={profile} type={type} />
            <Pad size={spacings.xs} />
            <View style={{ flexDirection: 'column' }}>
                {clickable && buttonText ? (
                    <TextButton
                        onPress={onProfile}
                        style={underline ? s.name : s.nameWithoutUnderline}
                        testID={isDeleted ? deletedUserLabel.defaultText : undefined}
                        text={buttonText}
                        variant='primary'
                    />
                ) : (
                    <UtilityText
                        label={isDeleted ? deletedUserLabel : undefined}
                        text={isDeleted ? undefined : displayName}
                        type='large'
                        underline={underline}
                        weight='medium'
                    />
                )}
                {subtitleLabel && (
                    <UtilityText
                        color={colors.colorTextSecondary}
                        formatParams={subtitleParams}
                        label={subtitleLabel}
                        underline={underline}
                    />
                )}
                {!subtitleLabel && showMetadata && (
                    <View>
                        <Pad size={2} />
                        <BylineMetadata edited={edited} originalLanguage={originalLanguage} postId={postId} profile={profile} time={time} />
                    </View>
                )}
            </View>
        </View>
    );
}

const useBylineStyle = createThemedStyles((tokens) => ({
    outer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    right: {
        marginLeft: 8,
    },
    name: {
        fontFamily: tokens.fonts.fontFamilySansMedium,
        fontWeight: '500',
    },
    nameWithoutUnderline: {
        fontFamily: tokens.fonts.fontFamilySansMedium,
        fontWeight: '500',
        textDecorationLine: 'none',
    },
}));

export type FaceSelectProps = {
    selected: boolean;
    onSelect: () => void;
    testID: string;
    children: React.ReactNode;
    ariaLabel?: string;
    disabled?: boolean;
};

export function FaceSelect({ selected, onSelect, testID, children, ariaLabel, disabled }: FaceSelectProps) {
    return (
        <FaceButton ariaLabel={ariaLabel} disabled={disabled} onPress={onSelect} selected={selected} testID={testID}>
            {children}
        </FaceButton>
    );
}

export type FaceButtonProps = {
    ariaLabel?: string;
    selected: boolean;
    onPress: () => void;
    testID?: string;
    children: React.ReactNode;
    disabled?: boolean;
};

export function FaceButton({ ariaLabel, selected, onPress, testID, children, disabled }: FaceButtonProps) {
    const s = useFaceButtonStyles();
    const disabledStyle = { opacity: 0.6 };

    return (
        <HoverView
            aria-checked={selected}
            ariaText={ariaLabel}
            disabled={disabled}
            onPress={onPress}
            role='radio'
            style={[s.ring, disabled ? disabledStyle : null]}
            testID={testID}
        >
            <View style={selected ? s.blackRing : s.whiteRing}>
                <View style={s.whiteRing}>{children}</View>
            </View>
        </HoverView>
    );
}

const useFaceButtonStyles = createThemedStyles((tokens) => ({
    ring: {
        alignSelf: 'flex-start',
    },
    whiteRing: {
        borderWidth: 3,
        borderColor: tokens.colors.colorSurfacePrimary,
        borderRadius: 50,
    },
    blackRing: {
        borderWidth: 3,
        borderColor: tokens.colors.colorButtonPrimary,
        borderRadius: 50,
    },
}));

const sizeMap = {
    huge: 80,
    large: 40,
    medium: 36,
    small: 32,
    tiny: 24,
};

const checkPadMap = {
    huge: 2,
    large: 2,
    medium: 3,
    small: 4,
    tiny: 8,
};
